/**
 * SafeCurves enums + specialised geodata shapes.
 *
 * The generic shapes (`IFeature<P, M>`, etc.) live in
 * `#lib/defines.ts` and are app-agnostic. This file provides the SafeCurves
 * enums and re-exports SafeCurves-narrowed aliases under the names the rest
 * of the SafeCurves code uses.
 *
 * `ISafeCurvesSystemLayers` is derived from the `moduleTable` in
 * `layerConfig.ts` and re-exported here so existing import paths keep
 * resolving. `ISafeCurvesSummaryData` stays a hand-written interface.
 */

import type { IFeature as IGenericFeature } from '#lib/types.js';

export enum ESafeCurvesModule {
	Identify = 'Identify',
	Prioritize = 'Prioritize',
	Interventions = 'Interventions'
}

export enum EProduct {
	SafeCurves = 'SafeCurves'
}

export enum EIdentify {
	Identify = 'Identify'
}
export enum EPrioritize {
	Prioritize = 'Prioritize',
	// Non-selectable generalised corridors served as a separate tileset for a smoother zoomed-out view.
	PrioritizeDisplayOnly = 'SafeCurves Prioritize (simplified)',
	// Local Programs data (see `LayerEntry.local`), not a GeoServer feature.
	ProgramProjects = 'Program projects'
}
// Enum property is layer "title" from GeoServer
export enum EInterventions {
	CurveAdvisorySigns = 'Advisory speeds and signage',
	CurveAdvisorySpeeds = 'Curve Advisory speeds',
	// Derived from the Advisory Signs source; renders the SDF outline sprite as a halo behind each sign.
	CurveAdvisorySignsHalo = 'Advisory signs halo',
	PavementFrictionFocusAreas = 'Pavement Friction Focus Areas',
	SpeedLimitReview = 'Speed Limit Review',
	StoppingSightDistance = 'Stopping Sight Distance',
	SSDObstructions = 'Stopping Sight Distance obstruction',
	PassingSightDistance = 'Passing Sight Distance',
	ClearZone = 'Clear Zone',
	// Local Programs data (see `LayerEntry.local`), not GeoServer features.
	ExistingSigns = 'Existing signs',
	ExistingSpeedPlaques = 'Existing speed plaques',
	ExistingChevronAreas = 'Existing chevron areas',
	ExistingBarriers = 'Existing barriers'
}

/** layer-id union. Fulfils the generic `<L extends string>` slot. */
export type SafeCurvesLayerId = EIdentify | EPrioritize | EInterventions;

/** operational feature — generic IFeature parameterised with EProduct + ESafeCurvesModule. */
export type ISafeCurvesFeature = IGenericFeature<EProduct, ESafeCurvesModule>;

// Derived from `moduleTable` in layerConfig.ts; re-exported type-only so existing
// importers (e.g. legendHelpers.test.ts) keep resolving this path.
export type { ISafeCurvesSystemLayers } from './layerConfig';

export interface ISafeCurvesSummaryData {
	location_label: string;
	[ESafeCurvesModule.Identify]?: Record<string, string | number>[];
	[ESafeCurvesModule.Prioritize]?: Record<string, string | number>[];
	[ESafeCurvesModule.Interventions]?: Record<string, string | number>[];
}
